import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {DcpJwtPayload} from '@app/model/user/dcp-jwt-playload';
import {CacheUpdateService} from '@app/services/cache-update.service';
import {DcpTransactionService} from '@app/services/dcp-transaction.service';
import {ConfigService, TransactionList} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthStorageService, GlobalMessageService, GlobalMessageType, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import jwt_decode from 'jwt-decode';
import {isString} from 'lodash-es';
import {Observable, of} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'dcp-contract-list-page',
  templateUrl: './transaction-list-page.component.html',
  styleUrls: ['./transaction-list-page.component.scss'],
})
export class TransactionListPageComponent implements OnInit {

  public sUserId: string = '';
  public currentUser: string;
  public isAuthorized: boolean;
  public user$: Observable<User>;
  public transactions$: Observable<TransactionList>;
  public previewUrl: string;
  public loadingState: boolean = false;
  public loadingScreenText: string = 'Please wait while the cached list is updated';
  public currentUserScimUserId: string;

  constructor(
    private dcpTransactionService: DcpTransactionService,
    private userFacade: UserAccountFacade,
    private globalMessageService: GlobalMessageService,
    private authStorageService: AuthStorageService,
    private configService: ConfigService,
    private cacheUpdateService: CacheUpdateService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.user$ = this.userFacade.get()

    this.user$.pipe(
      untilDestroyed(this),
      take(1)
    ).subscribe(user => {
      this.currentUserScimUserId = user.scimUserId
    });

    this.getMyTransactions();
    this.getAuthorizedRoles();
  }

  onInput(event: CustomEvent): void {
    const target = event.target as HTMLInputElement;
    this.sUserId = target.value
  }

  onReset() {
    this.sUserId = ''
    this.transactions$ = of(undefined);
  }

  getMyTransactions() {
    if (!/^S|^P/.test(this.currentUserScimUserId)) return;

    this.getTransactions()
  };

  getTransactions(userId?: string): void {
    this.dcpTransactionService.getTransactions(userId)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response) => {
          this.currentUser = userId || this.currentUserScimUserId;
          this.transactions$ = of(response);
        },
        (error) => {
          this.currentUser = undefined;
          this.transactions$ = of(undefined);
          this.globalMessageService.add(`${error?.error?.message} (${error?.message})`, GlobalMessageType.MSG_TYPE_ERROR, 60000);
        },
      );
  }

  getAuthorizedRoles(): void {
    this.authStorageService.getToken()
      .pipe(untilDestroyed(this))
      .subscribe(userToken => {
        const roles: Array<string> = jwt_decode<DcpJwtPayload>(userToken.access_token).company_groups;
        if (roles && isString(roles)) {
          this.isAuthorized = this.configService.getStringArray('authentication.roles')?.includes(roles);
        } else {
          this.isAuthorized = roles?.some(r => this.configService.getStringArray('authentication.roles')?.includes(r));
        }
      });
  }

  hasPreviewUrl(): boolean {
    return this.previewUrl?.length > 0;
  }

  togglePreview(targetUrl: string): void {
    this.previewUrl = this.previewUrl ? null : targetUrl;
    this.cdr.detectChanges();
  }

  convertUrlToCurrentHostname(url: string): string {
    const urlObject = new URL(url);
    urlObject.hostname = window.location.hostname;
    urlObject.port = window.location.port;
    return urlObject.href;
  }

  updateCache(userId?: string): void {
    this.loadingState = true;
    this.cacheUpdateService.updateTransactionsCache(userId, 'user')
      .pipe(untilDestroyed(this),
        switchMap(async () => this.cacheUpdateService.updateTransactionsCache(userId, 'customer').pipe(untilDestroyed(this))
          .subscribe(
            () => {
              this.currentUser = userId;
            },
            (error) => {
              this.currentUser = undefined;
              this.globalMessageService.add(`${error?.error?.message} (${error?.message})`, GlobalMessageType.MSG_TYPE_ERROR, 60000);
            }
          )),
      ).subscribe(
      () => {
        this.currentUser = userId;
        this.loadingState = false;
      },
      (error) => {
        this.currentUser = undefined;
        this.loadingState = false;
        this.globalMessageService.add(`${error?.error?.message} (${error?.message})`, GlobalMessageType.MSG_TYPE_ERROR, 60000);
      },
    );
  }
}
